// src/components/DottedLineBarChart.jsx
import { useEffect, useRef } from "react";
import {
  Chart,
  BarElement, LineElement, PointElement,
  BarController, LineController,
  CategoryScale, LinearScale,
  Tooltip, Legend,
} from "chart.js";

Chart.register(
  BarElement, LineElement, PointElement,
  BarController, LineController,
  CategoryScale, LinearScale,
  Tooltip, Legend
);

const BORDER        = "1px solid #ffffff";
const ROW_H         = 20;   // ⬇ 22 -> 20
const CHART_H       = 200;  // ⬇ 200 -> 180
const MIN_LABEL_COL = 220;

const LEGEND = [
  { label: "Total Revenue",                    type: "box",  color: "#5C9EDB" },
  { label: "EBITDA",                           type: "box",  color: "#EF7421" },
  { label: "OPERATING PROFIT/(LOSS)",          type: "dash", color: "#FFB000" },
  { label: "EBITDA Margin",                    type: "line", color: "#A5A5A5" },
  { label: "NET PROFIT MARGIN",               type: "line", color: "#F4B183" },
  { label: "Linear (OPERATING PROFIT/(LOSS))", type: "dash", color: "#FF0000" },
];

function Swatch({ type, color }) {
  const base = { display: "inline-block", flexShrink: 0, marginRight: 4, verticalAlign: "middle" };
  if (type === "box")
    return <span style={{ ...base, width: 20, height: 10, background: color, borderRadius: 2 }} />;
  if (type === "dash")
    return <span style={{ ...base, width: 20, height: 0, borderTop: `4px dashed ${color}`, marginTop: 3 }} />;
  return <span style={{ ...base, width: 20, height: 2.5, background: color, marginTop: 3 }} />;
}

// Linear regression helper
function calcLinearTrend(data) {
  const n     = data.length;
  if (n === 0) return [];
  const meanX = (n - 1) / 2;
  const meanY = data.reduce((a, b) => a + b, 0) / n;
  const slope = data.reduce((s, y, i) => s + (i - meanX) * (y - meanY), 0)
              / data.reduce((s, _, i) => s + (i - meanX) ** 2, 0);
  return data.map((_, i) => Math.round(slope * i + (meanY - slope * meanX)));
}

export default function DottedLineBarChart({
  labels          = [],
  totalRevenue    = [],
  ebitda          = [],
  opProfit        = [],
  ebitdaMargin    = [],
  netProfitMargin = [],
  title           = "ALL COMPANIES Prior Year Performance & 5 Year MTP - Reporting Currency",
  subtitle        = "",
  // Agar BoardPackLayout se available height pass karni ho to ye prop use karo.
  // Diya na jaye to default CHART_H (230) use hoga jo already 554px area ke liye fit hai.
  containerHeight = null,
}) {
  const canvasRef    = useRef(null);
  const chartRef     = useRef(null);
  const containerRef = useRef(null);
  const tableRef      = useRef(null);
  const labelColRef   = useRef(null);

  const linearOp = calcLinearTrend(opProfit);

  const formatNumber = (v) => {
  if (typeof v !== "number") return v;
  return v < 0 ? `(${Math.abs(v).toLocaleString()})` : v.toLocaleString();
};

const formatPercent = (v) => {
  if (typeof v !== "number") return v;
  return v < 0
    ? `(${Math.abs(v).toFixed(1)}%)`
    : `${Number(v).toFixed(1)}%`;
};

  const TABLE_ROWS = [
    { label: "Total Revenue",           values: totalRevenue,    swatchType: "box",  color: "#5C9EDB", fmt: formatNumber },
    { label: "EBITDA",                  values: ebitda,          swatchType: "box",  color: "#EF7421", fmt: formatNumber },
    { label: "OPERATING PROFIT/(LOSS)", values: opProfit,        swatchType: "dash", color: "#FFBB00", fmt: formatNumber },
    { label: "EBITDA Margin",           values: ebitdaMargin,    swatchType: "line", color: "#A5A5A5", fmt: formatPercent },
    { label: "NET PROFIT MARGIN",       values: netProfitMargin, swatchType: "line", color: "#F4B183", fmt: formatPercent },
  ];

  // Table + legend + title ka fixed overhead height (approx)
  const TITLE_H  = 35;
  const TABLE_H  = ROW_H * (TABLE_ROWS.length + 1); // +1 header row
  const LEGEND_H = 30;
  const PADDING_H = 40; // outer + inner paddings total

  // Agar containerHeight diya gaya hai to chart height dynamically calculate karo,
  // warna default CHART_H use karo.
  const resolvedChartH = containerHeight
    ? Math.max(150, containerHeight - TITLE_H - TABLE_H - LEGEND_H - PADDING_H)
    : CHART_H;

  useEffect(() => {
    if (!canvasRef.current || labels.length === 0) return;
    if (chartRef.current) chartRef.current.destroy();

    // ─── Dynamic LEFT Y-axis min (supports negatives) ──────────────
    const allLeftValues = [
      ...(totalRevenue || []),
      ...(ebitda       || []),
      ...(opProfit     || []),
      ...(linearOp     || []),
    ].filter((v) => typeof v === "number" && !isNaN(v));

    const minLeft = allLeftValues.length ? Math.min(...allLeftValues) : 0;

    // Add ~10% padding below the minimum negative value so bars don't touch the edge
    const yLeftMin = minLeft < 0
      ? Math.floor(minLeft * 1.1 / 100000) * 100000
      : 0;

    // ─── Dynamic RIGHT Y-axis max ──────────────────────────────────
    const allRightValues = [
      ...(ebitdaMargin    || []),
      ...(netProfitMargin || []),
    ].filter((v) => typeof v === "number" && !isNaN(v));

    const minRight = allRightValues.length ? Math.min(...allRightValues) : 0;
    const maxRight = allRightValues.length ? Math.max(...allRightValues) : 100;
    const yRightMax = Math.ceil(maxRight / 10) * 10;

    // If right axis also has negatives, calculate its min too
    const yRightMin = minRight < 0 ? Math.floor(minRight / 10) * 10 : 0;

    // ─── alignTablePlugin ── keeps the table's columns lined up under the
    // matching bars by measuring the chart's real plot-area pixels and sizing
    // the table's label column + overall width to match it exactly.
    const alignTablePlugin = {
      id: "alignTable",
      afterLayout(chart) {
        const area = chart.chartArea;
        if (!area || !containerRef.current || !tableRef.current) return;

        const canvasRect = containerRef.current.getBoundingClientRect();
        const tableRect = tableRef.current.getBoundingClientRect();

        const targetLeft = canvasRect.left + area.left;
        const targetRight = canvasRect.left + area.right;

        const labelWidth = Math.max(MIN_LABEL_COL, targetLeft - tableRect.left);
        const totalWidth = labelWidth + (targetRight - targetLeft);

        tableRef.current.style.width = `${totalWidth}px`;
        if (labelColRef.current) {
          labelColRef.current.style.width = `${labelWidth}px`;
        }
      },
    };

    chartRef.current = new Chart(canvasRef.current, {
      data: {
        labels,
        datasets: [
          {
            type: "bar",
            label: "Total Revenue",
            data: totalRevenue,
            backgroundColor: "#5C9EDB",
            yAxisID: "yLeft",
            order: 3,
            categoryPercentage: 0.7,
            barPercentage: 0.8,
          },
          {
            type: "bar",
            label: "EBITDA",
            data: ebitda,
            backgroundColor: "#ed7d31",
            yAxisID: "yLeft",
            order: 3,
            categoryPercentage: 0.7,
            barPercentage: 0.8,
          },
          {
  type: "line",
  label: "OPERATING PROFIT/(LOSS)",
  data: opProfit,
  borderColor: "#FFB000",
  borderDash: [10, 20],        // simple single dashes, original jaisa
  borderWidth: 4,
  borderCapStyle: "round",   // rounded caps — yahi tune manga tha
  pointRadius: 0,
  fill: false,
  yAxisID: "yLeft",
  order: 2,
  tension: 0,
},
          {
            type: "line",
            label: "EBITDA Margin",
            data: ebitdaMargin,
            borderColor: "#A5A5A5",
            borderWidth: 2.5,
            pointRadius: 0,
            fill: false,
            yAxisID: "yRight",
            order: 1,
            tension: 0,
          },
          {
            type: "line",
            label: "NET PROFIT MARGIN",
            data: netProfitMargin,
            borderColor: "#F4B183",
            borderWidth: 2,
            pointRadius: 0,
            fill: false,
            yAxisID: "yRight",
            order: 1,
            tension: 0,
          },
          {
            type: "line",
            label: "Linear",
            data: linearOp,
            borderColor: "#FF0000",
            borderWidth: 2,
            borderDash: [8, 5],
            pointRadius: 0,
            fill: false,
            yAxisID: "yLeft",
            order: 2,
            tension: 0,
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
                devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),

        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0,0,0,0.85)",
            titleColor: "#fff",
            bodyColor: "#ccc",
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.y;
                if (ctx.dataset.yAxisID === "yRight")
                  return ` ${ctx.dataset.label}: ${Number(v).toFixed(1)}%`;
                return ` ${ctx.dataset.label}: ${v.toLocaleString()}`;
              },
            },
          },
        },
        scales: {
          x: {
            display: false,
            grid: { display: false },
            offset: true,
          },
          yLeft: {
            position: "left",
            min: yLeftMin,   // ✅ FIX: dynamic min — supports negative values
            grid: {
  display: true,
  drawBorder: false,
  color: (ctx) => {
    // sirf zero line show karo
    return ctx.tick.value === 0
      ? "rgba(255,255,255,0.5)"
      : "transparent";
  },
  lineWidth: (ctx) => (ctx.tick.value === 0 ? 1.5 : 0),
},
            ticks: {
              color: "#ffffff",
              font: { size: 10 },
              maxTicksLimit: 8,
              callback: (v) => v.toLocaleString(),
            },
          },
          yRight: {
  position: "right",
  min: yRightMin,
  max: yRightMax,
  grid: { display: false },
  ticks: {
    display: true,
    color: "#ffffff",
    font: { size: 10 },
    padding: 10,   // ✅ ye add karo (default 3 hota hai)
    callback: (v) => v + "%",
  },
},
        },
        layout: {
          padding: { top: 10, bottom: 0, left: MIN_LABEL_COL, right: 20 },
        },
      },
      plugins: [alignTablePlugin],
    });

   const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartRef.current) return;
  const w = containerRef.current.offsetWidth;
  const h = containerRef.current.offsetHeight;
  chartRef.current.resize(w, h);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  chartRef.current?.destroy();
};
  }, [labels, totalRevenue, ebitda, opProfit, ebitdaMargin, netProfitMargin, resolvedChartH]);

  return (
    <div className="px-[8px] py-[6px]">
      <div
        style={{
          borderRadius: 8,
          backgroundColor: "#464647",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Title */}
        <div style={{ textAlign: "center", padding: "4px 20px" }}>{/* ⬇ 6px -> 4px */}
          {title && (
            <div style={{ fontSize: 13, fontWeight: 700, color: "#ffffff" }}>
              {title}
            </div>
          )}
          {subtitle && (
            <div style={{ fontSize: 11, fontWeight: 400, color: "#cfcfcf", marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>

        <div style={{ padding: "4px 10px" }}>{/* ⬇ 6px 10px -> 4px 10px */}
          {/* Chart */}
          <div ref={containerRef} style={{ width: "100%", position: "relative", height: resolvedChartH }}>
            <canvas ref={canvasRef} />
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto", textAlign: "left" }}>
            <table
              ref={tableRef}
              style={{
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col ref={labelColRef} style={{ width: MIN_LABEL_COL }} />
                {labels.map((_, i) => (
                  <col key={i} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  {labels.map((lbl) => (
                    <th
                      key={lbl}
                      style={{
                        textAlign: "center",
                        padding: "0 2px",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 600,
                        height: ROW_H,
                        border: BORDER,
                      }}
                    >
                      {lbl}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((row) => (
                  <tr key={row.label}>
                    <td
                      style={{
                        height: ROW_H,
                        padding: "0 6px",
                        border: BORDER,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Swatch type={row.swatchType} color={row.color} />
                        <span style={{ color: "#fff", fontSize: 10 }}>
                          {row.label}
                        </span>
                      </span>
                    </td>
                    {row.values.map((val, i) => (
                      <td
                        key={i}
                        style={{
                          textAlign: "center",
                          padding: "0 2px",
                          // ✅ Highlight negative values in red for quick visual scan
                          color: typeof val === "number" && val < 0 ? "#FF6B6B" : "#fff",
                          fontSize: 10,
                          height: ROW_H,
                          border: BORDER,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.fmt(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="flex justify-center items-center my-1">{/* ⬇ my-2 -> my-1 */}
            {LEGEND.map((item) => (
              <div
                key={item.label}
                className="flex justify-center text-center items-center mx-4"
              >
                <Swatch type={item.type} color={item.color} />
                <span style={{ color: "#fff", fontSize: 10 }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}